import { uxrr } from '@zyno-io/uxrr-client';
import { defineStore } from 'pinia';
import { watch } from 'vue';

import type { IConfig, IDebtorInfo, IIdentity } from './generated/openapi-client';
import type { c2c } from './generated/proto/c2cagent';
import { Auth } from './shared/services/auth';

export interface ICall {
    provisional?: {
        sourceNumber?: string | null;
        remoteNumber: string;
        remoteName?: string;
        fileNumber?: string;
        isRescheduled?: boolean;
    };
    rpc?: c2c.agentServer.ICallInfo;
    hangupCause?: string;
    held?: boolean;
    // Wall-clock ms when this call was last set from a server push. Used on reconnect to tell a
    // stale/phantom call (predates the current connection) apart from one (re)confirmed after reconnect.
    updatedAt?: number;
}

export const useStore = defineStore('root', {
    state: () => ({
        globalError: null as string | null,
        user: null as IIdentity | null,
        srpcConflict: false,
        srpcDisconnectedByOther: false,
        srpcStatus: 'disconnected' as 'disconnected' | 'connecting' | 'connected',
        srpcConnectedAt: 0,
        sipStatus: 'disconnected' as 'disconnected' | 'connecting' | 'connected',
        activeCampaignId: null as number | null,
        activeCall: null as ICall | null,
        activeDebtor: null as IDebtorInfo | null,
        activeAccessLogId: null as number | null,
        nextDebtorSearch: null as string | null,
        debtorUpdatesMade: false,
        sipSpeakerDeviceId: localStorage.getItem('c2c:sipSpeakerDeviceId'),
        sipMicrophoneDeviceId: localStorage.getItem('c2c:sipMicrophoneDeviceId'),
        config: {} as Partial<IConfig>,
        callbackOptOut: false,
        callbacksAdminDisabled: false,
        inboundOptOut: false,
        showManualDialpad: false
    }),

    getters: {
        isSrpcConnected(state) {
            return state.srpcStatus === 'connected';
        },

        isSipConnected(state) {
            return state.sipStatus === 'connected';
        }
    }
});

export function initStore() {
    const store = useStore();

    watch(
        () => store.user,
        async () => {
            if (!store.user) return;

            uxrr.identify({
                userId: String(store.user.id),
                userEmail: store.user.email
            });
        }
    );

    watch(
        () => store.sipSpeakerDeviceId,
        () => {
            localStorage['c2c:sipSpeakerDeviceId'] = store.sipSpeakerDeviceId;
        }
    );
    watch(
        () => store.sipMicrophoneDeviceId,
        () => {
            localStorage['c2c:sipMicrophoneDeviceId'] = store.sipMicrophoneDeviceId;
        }
    );

    Auth.addEventListener('auth:cleared', () => {
        if (import.meta.env.VITE_APP_CF_LOGOUT_URL) {
            location.href = import.meta.env.VITE_APP_CF_LOGOUT_URL;
        }
    });
}
